import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateToAdminDashboard } from "../router/dashboardNavigation";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Box,
  Typography,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Pagination,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Route as RouteIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  ReceiptLong as ReceiptLongIcon,
} from "@mui/icons-material";

import {
  useGetTripsQuery,
  useAddTripMutation,
  useUpdateTripMutation,
  useStartTripMutation,
  useCompleteTripMutation,
  useCancelTripMutation,
  useDeleteTripMutation,
  useGetMaterialsQuery,
  useAddMaterialMutation,
  useUpdateMaterialMutation,
  useDeleteMaterialMutation,
  useGetDocumentsQuery,
  useAddDocumentMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
  useAddExpenseMutation,
  useGetClientsQuery,
  useAddClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
} from "../redux/logistics/logisticsApi";

import AddTripModal from "./modals/AddTripModal";
import EditTripModal from "./modals/EditTripModal";
import AddMaterialModal from "./modals/AddMaterialModal";
import EditMaterialModal from "./modals/EditMaterialModal";
import AddDocumentModal from "./modals/AddDocumentModal";
import AddExpenseModal from "./modals/AddExpenseModal";
import AddClientModal from "./modals/AddClientModal";
import EditClientModal from "./modals/EditClientModal";
import { ROLES } from "../utils/rbacHelper";
import { useGetAllUsersQuery } from "../redux/admin/dashboardApi";
import { useGetAllLiteDevicesQuery } from "../redux/admin/adminApi";
import ScrollableTableContainer from "./common/ScrollableTableContainer";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

// ✅ Format dates properly
function formatDate(value) {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('en-IN', options);
  } catch (e) {
    return value;
  }
}

function unwrapMongo(value) {
  // Handles: { $oid: "..." } and { $date: "..." }
  if (value && typeof value === "object") {
    if ("$oid" in value) return value.$oid;
    if ("$date" in value) return value.$date;
  }
  return value;
}

function toReadableLabel(key = "") {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function formatLocationLike(value) {
  if (!value || typeof value !== "object") return null;
  const parts = [];
  if (value.address) parts.push(String(value.address));
  if (value.area) parts.push(String(value.area));
  if (value.city) parts.push(String(value.city));
  if (value.state) parts.push(String(value.state));
  if (value.pincode) parts.push(String(value.pincode));
  if (value.country) parts.push(String(value.country));
  if (value.lat != null && value.lng != null) parts.push(`(${value.lat}, ${value.lng})`);
  return parts.length ? parts.join(", ") : null;
}

function formatEntityLike(value, lookups = {}) {
  if (!value || typeof value !== "object") return null;

  const resolvedId =
    (value._id && resolveLookupLabel(value._id, lookups)) ||
    (value.id && resolveLookupLabel(value.id, lookups));

  const primary =
    value.username ||
    value.name ||
    value.routeName ||
    value.title ||
    value.materialName ||
    value.expenseType ||
    value.sensorName ||
    value.deviceName ||
    value.clientName ||
    value.fileName ||
    value.originalName ||
    value.documentName ||
    value.type ||
    resolvedId;

  const secondary =
    value.email ||
    value.mobileNo ||
    value.phone ||
    value.deviceId ||
    value.imei ||
    value.uniqueId ||
    value.category;

  if (!primary && !secondary) return null;
  return [primary, secondary].filter(Boolean).join(" • ");
}

function isMongoIdLike(value) {
  return typeof value === "string" && /^[a-f0-9]{24}$/i.test(value.trim());
}

function resolveLookupLabel(value, lookups = {}) {
  const id = unwrapMongo(value);
  if (!id || !isMongoIdLike(String(id))) return null;

  return (
    lookups.usersById?.get(String(id)) ||
    lookups.materialsById?.get(String(id)) ||
    lookups.clientsById?.get(String(id)) ||
    lookups.devicesById?.get(String(id)) ||
    null
  );
}

function summarizePlainObject(value, lookups = {}) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const entitySummary = formatEntityLike(value, lookups);
  if (entitySummary) return entitySummary;

  const locationSummary = formatLocationLike(value);
  if (locationSummary) return locationSummary;

  const entries = Object.entries(value)
    .map(([k, v]) => [k, unwrapMongo(v)])
    .filter(([, v]) => v !== null && v !== undefined && v !== "" && typeof v !== "object")
    .slice(0, 4)
    .map(([k, v]) => `${toReadableLabel(k)}: ${resolveLookupLabel(v, lookups) || v}`);

  return entries.length ? entries.join(" • ") : "Details available";
}

function formatValue(value, key, lookups = {}) {
  if (value === null || value === undefined) return "—";

  // ✅ unwrap Mongo wrappers early
  value = unwrapMongo(value);

  // ✅ Treat source/destination as location objects
  const k = String(key || "").toLowerCase();
  const lookupLabel = resolveLookupLabel(value, lookups);
  if (lookupLabel) return lookupLabel;

  if (k === "source" || k === "destination") {
    if (typeof value === "object" && value) return formatLocationLike(value) || "—";
    return String(value);
  }

  // ✅ Handle ObjectId-like fields: createdBy, assignedToDevice, etc
  if (k.includes("by") || k.includes("assignedto")) {
    // unwrap again in case nested { $oid }
    const v = unwrapMongo(value);

    if (typeof v === "object" && v) {
      // if populated user
      if (v.username || v.name || v.email) return String(v.username || v.name || v.email);

      // if objectid stored in _id
      if (v._id) return resolveLookupLabel(v._id, lookups) || String(unwrapMongo(v._id));
      return "—";
    }
    return resolveLookupLabel(v, lookups) || String(v);
  }

  // ✅ Dates
  if (k.includes("date") || k.includes("time") || k.includes("at")) {
    const v = unwrapMongo(value);
    return formatDate(v);
  }

  // ✅ calibration / calliberation: array of objects
  if (k === "calibration" || k === "calliberation" || k.includes("calibr")) {
    if (Array.isArray(value)) {
      if (!value.length) return "—";
      // show first few points nicely
      return value
        .slice(0, 3)
        .map(p => `v:${p.value ?? "—"} volt:${p.voltage ?? "—"}`)
        .join(" | ") + (value.length > 3 ? ` (+${value.length - 3} more)` : "");
    }
    if (typeof value === "object" && value) {
      const rows = Array.isArray(value?.rows) ? value.rows : Array.isArray(value?.points) ? value.points : null;
      if (rows?.length) {
        return rows
          .slice(0, 3)
          .map((p) => `v:${p.value ?? "—"} volt:${p.voltage ?? "—"}`)
          .join(" | ") + (rows.length > 3 ? ` (+${rows.length - 3} more)` : "");
      }
    }
    if (typeof value === "object" && value) return summarizePlainObject(value, lookups);
    return String(value);
  }

  // ✅ Generic location/address objects even if key isn't "address"
  if (typeof value === "object" && value && (value.address || value.lat != null || value.lng != null)) {
    return formatLocationLike(value) || "—";
  }

  // ✅ Arrays
  if (Array.isArray(value)) {
    if (!value.length) return "—";
    if (typeof value[0] === "object" && value[0] !== null) {
      return value
        .slice(0, 3)
        .map((item) => summarizePlainObject(item, lookups))
        .filter(Boolean)
        .join(" | ") + (value.length > 3 ? ` (+${value.length - 3} more)` : "");
    }
    return value.map((item) => resolveLookupLabel(item, lookups) || item).join(", ");
  }

  // ✅ Booleans
  if (typeof value === "boolean") return value ? "Yes" : "No";

  // ✅ Objects fallback: NEVER return raw object/json dump
  if (typeof value === "object") return summarizePlainObject(value, lookups);

  return String(value);
}

function getStatusChipMeta(formattedValue, key) {
  const text = String(formattedValue || "").trim();
  const normalized = text.toLowerCase();

  if (
    ["status", "tripStatus", "paymentStatus", "approvalStatus", "state"].includes(key) ||
    key.toLowerCase().includes("status")
  ) {
    if (["completed", "active", "approved", "paid", "online", "valid"].includes(normalized)) {
      return { label: text, color: "success" };
    }
    if (["pending", "planned", "scheduled", "draft", "in_progress", "started"].includes(normalized)) {
      return { label: text.replaceAll("_", " "), color: "warning" };
    }
    if (["cancelled", "rejected", "expired", "offline", "failed"].includes(normalized)) {
      return { label: text, color: "error" };
    }
    return { label: text.replaceAll("_", " "), color: "default" };
  }

  if (key === "isActive" || key === "active") {
    return { label: normalized === "yes" ? "Active" : "Inactive", color: normalized === "yes" ? "success" : "default" };
  }

  return null;
}

function normalizeCollectionResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.expenses)) return payload.expenses;
  if (Array.isArray(payload.materials)) return payload.materials;
  if (Array.isArray(payload.licenses)) return payload.licenses;
  if (Array.isArray(payload.documents)) return payload.documents;
  if (Array.isArray(payload.routes)) return payload.routes;
  if (Array.isArray(payload.sensors)) return payload.sensors;
  if (Array.isArray(payload.reminders)) return payload.reminders;
  if (Array.isArray(payload.trips)) return payload.trips;
  if (Array.isArray(payload.clients)) return payload.clients;
  if (Array.isArray(payload.users)) return payload.users;
  return [];
}


// ✅ Get dynamic columns from data
function getDynamicColumns(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  
  const allKeys = new Set();
  const excludeKeys = ["_id", "id", "__v", "password", "token"];
  
  data.forEach(item => {
    Object.keys(item).forEach(key => {
      if (!excludeKeys.includes(key)) {
        allKeys.add(key);
      }
    });
  });
  
  // Sort: createdAt/updatedAt at end, rest alphabetically
  const columns = Array.from(allKeys).sort((a, b) => {
    const aIsDate = a.toLowerCase().includes("createdat") || a.toLowerCase().includes("updatedat");
    const bIsDate = b.toLowerCase().includes("createdat") || b.toLowerCase().includes("updatedat");
    
    if (aIsDate && !bIsDate) return 1;
    if (!aIsDate && bIsDate) return -1;
    
    return a.localeCompare(b);
  });
  
  return columns.map(key => ({
    key,
    label: key
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .replace(/^./, str => str.toUpperCase())
      .trim()
  }));
}

function getRowId(row) {
  return row?._id || row?.id || Math.random();
}

function resolveTripLifecycle(row) {
  const tripStatus = String(row?.tripStatus || "").trim().toLowerCase();
  if (tripStatus) return tripStatus;

  const legacy = String(row?.status || "").trim().toLowerCase();
  if (legacy === "scheduled") return "planned";
  if (legacy === "active") return "in_progress";
  if (legacy === "completed") return "completed";
  if (legacy === "cancelled") return "cancelled";
  return "planned";
}




const TABS = [
  { key: "trips", label: "Trips" },
  { key: "clients", label: "Clients" },
  { key: "materials", label: "Materials" },
  { key: "documents", label: "Documents" },
];

export default function LogisticsDashboard() {
  const navigate = useNavigate();
  const currentUserRole = String(useSelector((state) => state?.auth?.user?.role || "")).toLowerCase();
  const canManageLogistics = [
    ROLES.SUPER_ADMIN,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.MANAGER,
    ROLES.HR,
    ROLES.OFFICE_EMPLOYEE,
    ROLES.FIELD_EMPLOYEE,
    ROLES.USER,
    ROLES.CLIENT,
  ].includes(currentUserRole);
  const [activeTab, setActiveTab] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [tripActionModal, setTripActionModal] = useState({ type: null, row: null });
  const [shareDialog, setShareDialog] = useState({ open: false, item: null, mode: "email", links: null });
  const [shareEmail, setShareEmail] = useState("");
  const [expandedCells, setExpandedCells] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tablePage, setTablePage] = useState(1);

  const currentTabKey = TABS[activeTab].key;

  // ✅ QUERIES
  const tripsQ = useGetTripsQuery(undefined, { skip: currentTabKey !== "trips" });
  const materialsQ = useGetMaterialsQuery(undefined, { skip: currentTabKey !== "materials" });
  const documentsQ = useGetDocumentsQuery(undefined, { skip: currentTabKey !== "documents" });
  const clientsQ = useGetClientsQuery(undefined, { skip: currentTabKey !== "clients" });
  const allUsersQ = useGetAllUsersQuery();
  const allDevicesQ = useGetAllLiteDevicesQuery();
  const materialsLookupQ = useGetMaterialsQuery();

  // ✅ MUTATIONS
  const [addTrip] = useAddTripMutation();
  const [updateTrip] = useUpdateTripMutation();
  const [startTrip] = useStartTripMutation();
  const [completeTrip] = useCompleteTripMutation();
  const [cancelTrip] = useCancelTripMutation();
  const [deleteTrip] = useDeleteTripMutation();
  const [addMaterial] = useAddMaterialMutation();
  const [updateMaterial] = useUpdateMaterialMutation();
  const [deleteMaterial] = useDeleteMaterialMutation();
  const [addDocument] = useAddDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const [addExpense] = useAddExpenseMutation();
  const [deleteDocument] = useDeleteDocumentMutation();
  const [addClient] = useAddClientMutation();
  const [updateClient] = useUpdateClientMutation();
  const [deleteClient] = useDeleteClientMutation();

  // ✅ HANDLERS
  const handleAdd = () => setShowAddModal(true);

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleShare = (item) => {
    const getRouteCoords = (row) => {
      const src = row?.source || row?.startPoint || {};
      const dst = row?.destination || row?.endPoint || {};
      return {
        sLat: Number(src?.lat),
        sLng: Number(src?.lng),
        eLat: Number(dst?.lat),
        eLng: Number(dst?.lng),
      };
    };

    if (currentTabKey === "trips" || currentTabKey === "routes") {
      const { sLat, sLng, eLat, eLng } = getRouteCoords(item);
      if ([sLat, sLng, eLat, eLng].every(Number.isFinite)) {
        const origin = `${sLat},${sLng}`;
        const destination = `${eLat},${eLng}`;
        const routeLink = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
        setShareDialog({
          open: true,
          item,
          mode: "map",
          links: {
            routeLink,
            startLink: `https://www.google.com/maps?q=${origin}`,
            endLink: `https://www.google.com/maps?q=${destination}`,
          },
        });
        return;
      }
    }
    setShareDialog({ open: true, item, mode: "email", links: null });
    setShareEmail("");
  };

  const handleShowRouteInMap = (routeRow) => {
    const src = routeRow?.source || {};
    const dst = routeRow?.destination || {};
    const startPoint = routeRow?.startPoint || {};
    const endPoint = routeRow?.endPoint || {};
    const payload = {
      _id: routeRow?._id || routeRow?.id,
      routeName: routeRow?.routeName || routeRow?.name || "Route",
      source: {
        lat: Number(src?.lat ?? startPoint?.lat),
        lng: Number(src?.lng ?? startPoint?.lng),
        address: src?.address || routeRow?.startAddr || "",
      },
      destination: {
        lat: Number(dst?.lat ?? endPoint?.lat),
        lng: Number(dst?.lng ?? endPoint?.lng),
        address: dst?.address || routeRow?.endAddr || "",
      },
    };

    if (
      ![payload.source.lat, payload.source.lng, payload.destination.lat, payload.destination.lng].every(
        Number.isFinite,
      )
    ) {
      alert("Route source/destination coordinates are missing.");
      return;
    }

    navigateToAdminDashboard(navigate, {
      tab: "map",
      focusRoute: payload,
    });
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      alert("Link copied");
    } catch {
      window.prompt("Copy this link:", link);
    }
  };

  const shareLink = async (link, title = "Trip Route") => {
    try {
      if (navigator.share) {
        await navigator.share({ title, text: link, url: link });
      } else {
        await copyLink(link);
      }
    } catch {
      // user cancelled
    }
  };

  const handleShareSubmit = () => {
    if (!shareEmail) {
      alert("Please enter an email address");
      return;
    }
    
    // Add your share API call here
    console.log("Sharing with:", shareEmail, "Item:", shareDialog.item);
    alert(`Shared successfully with ${shareEmail}!`);
    setShareDialog({ open: false, item: null });
  };

  const toggleCellExpansion = (rowId, colKey) => {
    const cellId = `${rowId}-${colKey}`;
    setExpandedCells(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cellId)) {
        newSet.delete(cellId);
      } else {
        newSet.add(cellId);
      }
      return newSet;
    });
  };

  const isCellExpanded = (rowId, colKey) => {
    return expandedCells.has(`${rowId}-${colKey}`);
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const mutations = {
        trips: deleteTrip,
        clients: deleteClient,
        materials: deleteMaterial,
        documents: deleteDocument,
      };
      
      await mutations[currentTabKey]?.(id).unwrap();
      alert("Deleted successfully!");
    } catch (err) {
      console.error("Delete Error:", err);
      alert("Failed to delete: " + (err?.data?.error || err.message));
    }
  };

  const handleTripTransition = async (row, action) => {
    const id = getRowId(row);
    try {
      if (action === "start") {
        await startTrip(id).unwrap();
      } else if (action === "complete") {
        await completeTrip({ id }).unwrap();
      } else if (action === "cancel") {
        await cancelTrip({ id }).unwrap();
      }
    } catch (err) {
      alert(err?.data?.error || err?.data?.msg || "Trip action failed");
    }
  };

  const handleSubmitAdd = async (formData) => {
    try {
      const payload =
        currentTabKey === "documents"
          ? (() => {
              const fd = new FormData();
              Object.keys(formData || {}).forEach((key) => {
                if (formData[key] !== undefined && formData[key] !== null && formData[key] !== "") {
                  fd.append(key, formData[key]);
                }
              });
              return fd;
            })()
          : formData;

      const mutations = {
        trips: addTrip,
        clients: addClient,
        materials: addMaterial,
        documents: addDocument,
      };
      
      await mutations[currentTabKey]?.(payload).unwrap();
      alert("Added successfully!");
      setShowAddModal(false);
    } catch (err) {
      console.error("Add Error:", err);
      alert("Failed to add: " + (err?.data?.error || err.message));
    }
  };

  const handleSubmitEdit = async (id, formData) => {
    try {
      if (currentTabKey === "documents") {
        const fd = new FormData();
        Object.keys(formData || {}).forEach((key) => {
          if (formData[key] !== undefined && formData[key] !== null && formData[key] !== "") {
            fd.append(key, formData[key]);
          }
        });
        await updateDocument({ id, body: fd }).unwrap();
      } else {
        const mutations = {
          trips: updateTrip,
          clients: updateClient,
          materials: updateMaterial,
        };

        await mutations[currentTabKey]?.({ id, ...formData }).unwrap();
      }
      alert("Updated successfully!");
      setShowEditModal(false);
      setSelectedItem(null);
    } catch (err) {
      console.error("Update Error:", err);
      alert("Failed to update: " + (err?.data?.error || err.message));
    }
  };

  const openTripActionModal = (type, row) => {
    setTripActionModal({ type, row });
  };

  const closeTripActionModal = () => {
    setTripActionModal({ type: null, row: null });
  };

  const handleTripDocumentSubmit = async (formData) => {
    try {
      const fd = new FormData();
      Object.entries(formData || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          fd.append(key, value);
        }
      });
      const tripLabel =
        tripActionModal?.row?.routeName ||
        tripActionModal?.row?.client?.clientName ||
        tripActionModal?.row?._id ||
        "Trip";
      if (!fd.get("description")) {
        fd.append("description", `Trip document - ${tripLabel}`);
      }
      await addDocument(fd).unwrap();
      alert("Document added successfully!");
      closeTripActionModal();
    } catch (err) {
      console.error("Trip Document Add Error:", err);
      alert("Failed to add document: " + (err?.data?.error || err.message));
    }
  };

  const handleTripExpenseSubmit = async (formData) => {
    try {
      const fd = new FormData();
      Object.entries(formData || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          fd.append(key, value);
        }
      });
      await addExpense(fd).unwrap();
      alert("Expense added successfully!");
      closeTripActionModal();
    } catch (err) {
      console.error("Trip Expense Add Error:", err);
      alert("Failed to add expense: " + (err?.data?.error || err.message));
    }
  };

  // ✅ Get Current Data
  const getCurrentData = () => {
    const queryMap = {
      trips: tripsQ.data,
      clients: clientsQ.data,
      materials: materialsQ.data,
      documents: documentsQ.data,
    };
    
    const data = queryMap[currentTabKey];
    return normalizeCollectionResponse(data);
  };

  const getCurrentQuery = () => {
    const queries = {
      trips: tripsQ,
      clients: clientsQ,
      materials: materialsQ,
      documents: documentsQ,
    };
    return queries[currentTabKey] || { isLoading: false, isError: false };
  };

  const rows = getCurrentData();
  const query = getCurrentQuery();
  const columns = getDynamicColumns(rows);
  const queryErrorMessage =
    query?.error?.data?.error ||
    query?.error?.data?.msg ||
    query?.error?.error ||
    "Failed to load data";
  const lookupData = useMemo(() => {
    const users = normalizeCollectionResponse(allUsersQ.data);
    const devices = normalizeCollectionResponse(allDevicesQ.data);
    const materials = normalizeCollectionResponse(materialsLookupQ.data);
    const clients = normalizeCollectionResponse(clientsQ.data);

    return {
      usersById: new Map(
        users.map((user) => [
          String(user?._id || user?.id),
          user?.username || user?.name || user?.email || user?.mobileNo || String(user?._id || user?.id),
        ]),
      ),
      devicesById: new Map(
        devices.map((device) => [
          String(device?._id || device?.id),
          device?.name || device?.deviceName || device?.imei || device?.deviceId || String(device?._id || device?.id),
        ]),
      ),
      materialsById: new Map(
        materials.map((material) => [
          String(material?._id || material?.id),
          material?.name || material?.materialName || material?.category || String(material?._id || material?.id),
        ]),
      ),
      clientsById: new Map(
        clients.map((client) => [
          String(client?._id || client?.id),
          client?.clientName || client?.name || client?.contactPersonName || String(client?._id || client?.id),
        ]),
      ),
    };
  }, [allUsersQ.data, allDevicesQ.data, materialsLookupQ.data, clientsQ.data]);
  const filteredRows = useMemo(() => {
    const queryText = searchQuery.trim().toLowerCase();
    if (!queryText) return rows;

    return rows.filter((row) =>
      columns.some((col) =>
        String(formatValue(row?.[col.key], col.key, lookupData) || "")
          .toLowerCase()
          .includes(queryText)
      )
    );
  }, [columns, lookupData, rows, searchQuery]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const pagedRows = useMemo(() => {
    const start = (tablePage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, rowsPerPage, tablePage]);

  useEffect(() => {
    if (tablePage > totalPages) {
      setTablePage(1);
    }
  }, [tablePage, totalPages]);

  const handleDownloadCurrentData = () => {
    if (!filteredRows?.length) {
      alert("No data available to download.");
      return;
    }
    const headers = columns.map((c) => c.label);
    const csvRows = filteredRows.map((row) =>
      columns
        .map((col) => {
          const value = String(formatValue(row?.[col.key], col.key, lookupData) || "");
          return `"${value.replace(/"/g, '""')}"`;
        })
        .join(",")
    );
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${currentTabKey}-data-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ✅ Render Modals
  const renderAddModal = () => {
    if (!showAddModal) return null;

    const modals = {
      trips: <AddTripModal onClose={() => setShowAddModal(false)} onSubmit={handleSubmitAdd} />,
      clients: <AddClientModal onClose={() => setShowAddModal(false)} onSubmit={handleSubmitAdd} />,
      materials: <AddMaterialModal onClose={() => setShowAddModal(false)} onSubmit={handleSubmitAdd} />,
      documents: <AddDocumentModal onClose={() => setShowAddModal(false)} onSubmit={handleSubmitAdd} />,
    };
    
    return modals[currentTabKey];
  };

  const renderEditModal = () => {
    if (!showEditModal || !selectedItem) return null;

    const modals = {
      trips: <EditTripModal key={`edit-trip-${getRowId(selectedItem)}`} onClose={() => setShowEditModal(false)} onSubmit={handleSubmitEdit} tripData={selectedItem} />,
      clients: <EditClientModal key={`edit-client-${getRowId(selectedItem)}`} onClose={() => setShowEditModal(false)} onSubmit={handleSubmitEdit} clientData={selectedItem} />,
      materials: <EditMaterialModal key={`edit-material-${getRowId(selectedItem)}`} onClose={() => setShowEditModal(false)} onSubmit={handleSubmitEdit} materialData={selectedItem} />,
      documents: (
        <AddDocumentModal
          key={`edit-document-${getRowId(selectedItem)}`}
          onClose={() => setShowEditModal(false)}
          onSubmit={(formData) => handleSubmitEdit(getRowId(selectedItem), formData)}
          initialData={selectedItem}
          title="Edit Document"
          submitLabel="UPDATE"
        />
      ),
    };
    
    return modals[currentTabKey];
  };

  const renderTripActionModal = () => {
    if (!tripActionModal?.type) return null;

    const row = tripActionModal.row || {};
    const tripVehicleId = row?.vehicle?._id || row?.vehicle || "";

    if (tripActionModal.type === "document") {
      return (
        <AddDocumentModal
          key={`trip-document-${getRowId(row)}`}
          onClose={closeTripActionModal}
          onSubmit={handleTripDocumentSubmit}
          initialData={{
            name: row?.routeName || "",
            type: "Trip",
            description: row?.startAddr && row?.endAddr ? `${row.startAddr} → ${row.endAddr}` : "",
          }}
        />
      );
    }

    if (tripActionModal.type === "expense") {
      return (
        <AddExpenseModal
          key={`trip-expense-${getRowId(row)}`}
          onClose={closeTripActionModal}
          onSubmit={handleTripExpenseSubmit}
          initialData={{
            vehicle: tripVehicleId,
            date: new Date().toISOString().slice(0, 10),
            description: row?.routeName ? `Trip expense - ${row.routeName}` : "",
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      <Box sx={{ p: 2.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
        <Paper sx={{ mb: 1.5, borderRadius: 2, overflow: "hidden" }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              setSearchQuery("");
              setTablePage(1);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1.5, pt: 0.5 }}
          >
            {TABS.map((tab, idx) => (
              <Tab
                key={tab.key}
                label={tab.label}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 15,
                  minHeight: 46,
                }}
              />
            ))}
          </Tabs>
        </Paper>

        {query.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {query.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {queryErrorMessage}
          </Alert>
        )}

        {!query.isLoading && !query.isError && (
          <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ p: 1.5, borderBottom: "1px solid #e5e7eb" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr auto" }, gap: 1.5, alignItems: "center" }}>
                <TextField
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setTablePage(1);
                  }}
                  sx={inputSx}
                  InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  {canManageLogistics && (
                    <IconButton onClick={handleAdd} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                      <AddIcon />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            </Box>

            <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <FormControl size="small" sx={{ minWidth: 88 }}>
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setTablePage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1}>
                <IconButton onClick={() => query?.refetch?.()} sx={{ color: "#6b7280" }}>
                  <RefreshIcon />
                </IconButton>
                <IconButton onClick={handleDownloadCurrentData} sx={{ color: "#6b7280" }} disabled={!filteredRows.length}>
                  <DownloadIcon />
                </IconButton>
              </Stack>
            </Box>

            <ScrollableTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.key} sx={{ fontWeight: 'bold' }}>
                        {col.label}
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 'bold', width: 200 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pagedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No data found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedRows.map((row) => {
                      const id = getRowId(row);

                      return (
                        <TableRow key={id} hover>
                          {columns.map((col) => {
                            const value = row?.[col.key];
                            const formatted = formatValue(value, col.key, lookupData);
                            const statusMeta = getStatusChipMeta(formatted, col.key);
                            const isExpanded = isCellExpanded(id, col.key);
                            const shouldTruncate = formatted && formatted.length > 50;
                            
                            // Ensure formatted is always a string
                            const displayText = String(formatted || "—");

                            return (
                              <TableCell 
                                key={col.key}
                                onClick={() => shouldTruncate && toggleCellExpansion(id, col.key)}
                                sx={{ 
                                  cursor: shouldTruncate ? 'pointer' : 'default',
                                  maxWidth: 300,
                                  whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: isExpanded ? 'clip' : 'ellipsis'
                                }}
                              >
                                {statusMeta ? (
                                  <Chip
                                    size="small"
                                    label={statusMeta.label}
                                    color={statusMeta.color}
                                    variant={statusMeta.color === "default" ? "outlined" : "filled"}
                                    sx={{ textTransform: "capitalize" }}
                                  />
                                ) : (
                                  <Stack spacing={0.35}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: isExpanded ? 'clip' : 'ellipsis',
                                        fontWeight: col.key.toLowerCase().includes("name") ? 600 : 400,
                                      }}
                                    >
                                      {isExpanded ? displayText : (shouldTruncate ? truncateText(displayText) : displayText)}
                                    </Typography>
                                    {!isExpanded && shouldTruncate && (
                                      <Typography variant="caption" color="primary">
                                        Click to view more
                                      </Typography>
                                    )}
                                  </Stack>
                                )}
                              </TableCell>
                            );
                          })}

                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {canManageLogistics && (
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEdit(row)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              {currentTabKey === "trips" && (
                                <>
                                  <IconButton size="small" color="primary" onClick={() => openTripActionModal("document", row)}>
                                    <DescriptionIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="primary" onClick={() => openTripActionModal("expense", row)}>
                                    <ReceiptLongIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="primary" onClick={() => handleShowRouteInMap(row)}>
                                    <RouteIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                              {canManageLogistics && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </ScrollableTableContainer>

            <Box sx={{ px: 2, py: 1.75, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #e5e7eb" }}>
              <Typography sx={{ color: "#374151" }}>
                Showing {filteredRows.length ? (tablePage - 1) * rowsPerPage + 1 : 0} to {Math.min(tablePage * rowsPerPage, filteredRows.length)} of {filteredRows.length} entries
              </Typography>
              <Pagination page={tablePage} count={totalPages} onChange={(_, value) => setTablePage(value)} shape="rounded" />
            </Box>
          </Paper>
        )}

      {renderAddModal()}
      {renderEditModal()}
      {renderTripActionModal()}

        {/* Share Dialog */}
        <Dialog open={shareDialog.open} onClose={() => setShareDialog({ open: false, item: null, mode: "email", links: null })}>
          <DialogTitle>Share {TABS[activeTab].label.slice(0, -1)}</DialogTitle>
          <DialogContent>
            {shareDialog.mode === "map" && shareDialog.links ? (
              <Box sx={{ mt: 1, minWidth: 360, display: "grid", gap: 1 }}>
                <Button
                  startIcon={<RouteIcon />}
                  variant="outlined"
                  onClick={() => shareLink(shareDialog.links.routeLink, "Trip Route")}
                >
                  Share Route Link
                </Button>
                <Button
                  startIcon={<ContentCopyIcon />}
                  variant="outlined"
                  onClick={() => copyLink(shareDialog.links.routeLink)}
                >
                  Copy Route Link
                </Button>
                <Button
                  variant="text"
                  onClick={() => copyLink(shareDialog.links.startLink)}
                >
                  Copy Start Point
                </Button>
                <Button
                  variant="text"
                  onClick={() => copyLink(shareDialog.links.endLink)}
                >
                  Copy End Point
                </Button>
              </Box>
            ) : (
              <TextField
                autoFocus
                margin="dense"
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="user@example.com"
                sx={{ mt: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShareDialog({ open: false, item: null, mode: "email", links: null })}>
              Cancel
            </Button>
            {shareDialog.mode !== "map" && (
              <Button onClick={handleShareSubmit} variant="contained">
                Share
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
